import { cn } from '@/lib/utils'
import { cva, VariantProps } from 'class-variance-authority'
import { XIcon } from 'lucide-react'
import { toast as baseToast, ExternalToast, Toaster as Sonner, type ToasterProps } from 'sonner'
import { CircleCheckIcon } from '../icons/circle-check-icon'
import { WaitingIcon } from '../icons/waiting-icon'
import { WarningIcon } from '../icons/warning-icon'

export const Toaster = ({ className, ...props }: ToasterProps) => (
  <Sonner
    className={cn('toaster group list-none', className)}
    toastOptions={{ unstyled: true, className: 'w-full' }}
    position='top-right'
    duration={6500}
    offset={{ top: 16, right: 16 }}
    {...props}
  />
)

const toastVariants = cva(
  'min-h-[36px] flex gap-2 shadow-sidebar items-center grow min-w-full text-xs text-pretty font-[450] justify-between rounded-lg border border-neutral-50 px-2 bg-neutral-25',
  {
    variants: {
      variant: {
        error: '',
        warning: '',
        success: '',
      },
    },
  },
)

type ToastVariants = VariantProps<typeof toastVariants>
type ToastProps = {
  message: React.ReactNode
  icon: React.ReactNode
  options?: ExternalToast
}
type ToastFunction = (message: React.ReactNode, options?: ExternalToast) => string | number

const TOAST_CONFIGS = {
  error: {
    iconWrapper: 'bg-error-50',
    iconClass: 'text-error-300',
    Icon: WarningIcon,
  },
  warning: {
    iconWrapper: 'bg-warning-50',
    iconClass: 'text-warning-300',
    Icon: WaitingIcon,
  },
  success: {
    iconWrapper: 'bg-success-50',
    iconClass: 'text-success-300',
    Icon: CircleCheckIcon,
  },
} as const

const createToastFunction = (variant: keyof typeof TOAST_CONFIGS): ToastFunction => {
  return (message, options) => {
    const config = TOAST_CONFIGS[variant]
    return customToast({
      variant,
      message,
      icon: (
        <div className={cn('rounded-full', config.iconWrapper)}>
          <config.Icon className={config.iconClass} />
        </div>
      ),
      options,
    })
  }
}

export const toast = {
  error: createToastFunction('error'),
  warning: createToastFunction('warning'),
  success: createToastFunction('success'),
  dismiss: baseToast.dismiss,
}

function customToast({ variant, message, icon, options }: ToastVariants & ToastProps) {
  return baseToast.custom(
    (t) => (
      <div className={cn(toastVariants({ variant }), options?.className)}>
        <div className='flex items-center gap-2'>
          {icon}
          {message}
        </div>
        <button tabIndex={0} onClick={() => baseToast.dismiss(t)}>
          <XIcon className='size-5 text-neutral-200' />
        </button>
      </div>
    ),
    options,
  )
}
